<template>
  <ModalComponent
    class="cancel-review-modal"
    :submitButtonText="t('dashboard.portfolio.footer.confirm_cancel_btn')"
    :hideSubmitButton="true"
    @setForceToggle="setForceToggle"
    @close="close"
    size="xl"
  >
    <template #title>
      {{ t("dashboard.portfolio.footer.review_cancel_title") }}
    </template>

    <template #body>
      <!-- Vertical padding only: the modal-body already supplies the 1rem
           horizontal inset, so content lines up with the header title and the
           footer buttons instead of being indented an extra step. -->
      <div class="py-3">
        <RegionMismatchAdvisory v-if="hasRegionMismatch" class="mb-3" />

        <AnyAibCaveatNotice v-if="usesAnyAib" class="mb-3" />

        <CancellationRuleWarningList class="mb-3" :groups="ruleWarningGroups" />

        <UnsupportedRegistryNotice
          v-if="modeRequiredEntries.length > 0"
          class="mb-3"
          :entries="modeRequiredEntries"
        >
          <CancellationModeChoice v-model="cancellationMode" class="mt-3" />
          <p
            v-if="cancellationMode === CancellationMode.ANY_AIB_REGISTRY"
            class="small mt-3 mb-0"
            data-test="any-aib-mode-caveat"
          >
            {{ t("cancellation_rules.any_aib_notice.message") }}
          </p>
        </UnsupportedRegistryNotice>

        <div v-if="hasInsufficientInventory" class="error-note mb-3">
          <FontAwesomeIcon icon="exclamation-triangle" class="flex-shrink-0" />
          <span class="fw-semibold">{{
            t("dashboard.portfolio.footer.risk_insufficient_inventory")
          }}</span>
        </div>

        <!-- Shared settings · eyebrow kicker + clean bordered panel, mirroring
             the editor's "Shared Settings" sub-section typography. -->
        <div class="eyebrow-label mb-2">
          {{ t("go_cancel.shared_defaults_title") }}
        </div>
        <div class="border rounded-3 p-3 mb-4">
          <div class="row g-3">
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("go_cancel.form.beneficiary_type.label") }}
              </div>
              <div class="fw-medium">
                {{
                  t(
                    `go_cancel.form.beneficiary_type.${sharedDefaults.beneficiary_type}`
                  )
                }}
              </div>
            </div>
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("go_cancel.form.period_start.label") }}
              </div>
              <div class="fw-medium">{{ sharedDefaults.period_start }}</div>
            </div>
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("go_cancel.form.period_end.label") }}
              </div>
              <div class="fw-medium">{{ sharedDefaults.period_end }}</div>
            </div>
          </div>

          <div class="mt-3 pt-3 border-top">
            <div class="text-secondary small mb-1">
              {{ t("go_cancel.form.global_operation_notes.label") }}
            </div>
            <div class="fw-medium">{{ globalOperationNotes || "-" }}</div>
          </div>
        </div>

        <!-- Cancellation statements · eyebrow kicker + a border-rounded
             DataTable. `border rounded` triggers the app-wide
             `.pv-datatable.rounded { overflow: hidden }` clip so the last row's
             corners no longer poke past the rounded border. -->
        <div class="eyebrow-label mb-2">
          {{ t("go_cancel.statements_title") }}
        </div>
        <TruncatedTextReveal ref="textReveal" class="review-table">
          <DataTable
            :value="materializedStatements"
            dataKey="temp_id"
            class="p-datatable-sm border rounded"
            scrollable
            scrollHeight="min(460px, 50vh)"
            :virtualScrollerOptions="
              materializedStatements.length > 10
                ? virtualScrollerOptions
                : undefined
            "
          >
            <Column :header="t('go_cancel.form.beneficiary_name.label')">
              <template #body="{ data }">
                <span class="review-text text-truncate" tabindex="0">{{
                  data.beneficiary_name
                }}</span>
                <TgeStatementOptionsSummary :options="data.tge_options" />
              </template>
            </Column>
            <Column :header="t('go_cancel.form.registry_country_region.label')">
              <template #body="{ data }">
                <div class="review-registry">
                  <span class="review-text text-truncate" tabindex="0">
                    {{ regionLabel(data.registry_country_region) }}
                  </span>
                  <PrivateStatementPill
                    v-if="isStatementPrivate(data.grexel_options)"
                  />
                </div>
              </template>
            </Column>
            <Column
              :header="t('go_cancel.form.registry_code.short_label')"
              style="width: 130px"
            >
              <template #body="{ data }">
                <span class="review-text text-truncate" tabindex="0">{{
                  data.registry_code || "-"
                }}</span>
              </template>
            </Column>
            <Column :header="t('go_cancel.form.address.label')">
              <template #body="{ data }">
                <span class="review-text text-truncate" tabindex="0">{{
                  data.address || "-"
                }}</span>
              </template>
            </Column>
            <Column header="MWh" style="width: 80px">
              <template #body="{ data }">
                {{ data.mwh_cancelled }}
              </template>
            </Column>
            <Column
              :header="t('go_cancel.form.cancellation_purpose_text.label')"
              style="min-width: 220px"
            >
              <template #body="{ data }">
                <span class="review-text text-truncate" tabindex="0">{{
                  data.cancellation_purpose || "-"
                }}</span>
              </template>
            </Column>
          </DataTable>
        </TruncatedTextReveal>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn-ghost" @click="close">
        {{ t("common.back") }}
      </button>
      <button
        type="button"
        class="btn btn-danger"
        :disabled="confirmDisabled || isLoading || !isModeChosen"
        @click="confirm"
      >
        <span
          v-if="isLoading"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ t("dashboard.portfolio.footer.confirm_cancel_btn") }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import TruncatedTextReveal from "@/components/common/TruncatedTextReveal.vue";
import { CancellationMode } from "@/dashboard/inventory_management/bulk_buy/bulkBuyTypes";
import AnyAibCaveatNotice from "@/dashboard/inventory_management/cancel/AnyAibCaveatNotice.vue";
import CancellationRuleWarningList from "@/dashboard/inventory_management/cancel/CancellationRuleWarningList.vue";
import type { CancellationRuleWarning } from "@/dashboard/inventory_management/cancel/cancellationRulesTypes";
import PrivateStatementPill from "@/dashboard/inventory_management/cancel/PrivateStatementPill.vue";
import TgeStatementOptionsSummary from "@/dashboard/inventory_management/cancel/TgeStatementOptionsSummary.vue";
import RegionMismatchAdvisory from "@/dashboard/inventory_management/cancel/RegionMismatchAdvisory.vue";
import { useCancellationTargets } from "@/dashboard/inventory_management/cancel/useCancellationTargetsStore";
import { useStatementRuleWarningGroups } from "@/dashboard/inventory_management/cancel/useStatementRuleWarningGroups";
import {
  type CancelSharedDefaults,
  isStatementPrivate,
} from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import CancellationModeChoice from "@/dashboard/inventory_management/common/CancellationModeChoice.vue";
import UnsupportedRegistryNotice, {
  type UnsupportedRegistryEntry,
} from "@/dashboard/inventory_management/common/UnsupportedRegistryNotice.vue";
import type { IndividualCancelStatement } from "@/types";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    hasRegionMismatch: boolean;
    /** The editor's live rule warnings, by statement `temp_id`. */
    ruleWarningsByTempId?: Record<string, CancellationRuleWarning[]>;
    hasInsufficientInventory: boolean;
    isLoading: boolean;
    confirmDisabled: boolean;
    materializedStatements: IndividualCancelStatement[];
    sharedDefaults: CancelSharedDefaults;
    globalOperationNotes: string | null;
  }>(),
  { ruleWarningsByTempId: () => ({}) }
);

const emit = defineEmits<{
  /** `cancellationMode` is null when no statement needs one. */
  confirm: [cancellationMode: CancellationMode | null];
}>();

const { t } = useI18n();
const targets = useCancellationTargets();

// One choice covers every statement whose region Soldera can't cancel in directly, as in bulk-buy (DEV-626 change 8).
const cancellationMode = ref<CancellationMode | null>(null);

const modeRequiredEntries = computed<UnsupportedRegistryEntry[]>(() =>
  props.materializedStatements
    .filter((statement) =>
      targets.value.requiresCancellationMode(statement.registry_country_region)
    )
    .map((statement, index) => ({
      key: `${statement.temp_id}-${index}`,
      name: statement.beneficiary_name,
      region: statement.registry_country_region ?? "",
    }))
);

const usesAnyAib = computed(() =>
  props.materializedStatements.some((statement) => statement.is_any_aib)
);

const isModeChosen = computed(
  () =>
    modeRequiredEntries.value.length === 0 || cancellationMode.value !== null
);

const regionLabel = (region: string | null | undefined): string =>
  targets.value.label(region, t);

const { groups: ruleWarningGroups } = useStatementRuleWarningGroups({
  statements: () => props.materializedStatements,
  warningsByKey: () => props.ruleWarningsByTempId,
  label: (statement) =>
    `${statement.beneficiary_name} (${regionLabel(statement.registry_country_region)})`,
  // The route-choice panel lists every statement whose region isn't direct, so its status warning would only say
  // the same thing twice here; the editor still shows it.
  shows: (warning) => warning.rule !== "direct_cancellation",
});
// PrimeVue positions virtual rows using itemSize, so content must not change row height.
const ROW_HEIGHT = 48;
const virtualScrollerOptions = { itemSize: ROW_HEIGHT, numToleratedItems: 5 };
const textReveal = useTemplateRef("textReveal");

const forceToggle = ref<((value: boolean) => void) | null>(null);

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
};

const open = () => {
  cancellationMode.value = null;
  forceToggle.value?.(true);
};

const close = () => {
  textReveal.value?.hide();
  forceToggle.value?.(false);
};

const confirm = () => {
  close();
  emit(
    "confirm",
    modeRequiredEntries.value.length > 0 ? cancellationMode.value : null
  );
};

defineExpose({ open, close });
</script>

<style scoped lang="scss">
.cancel-review-modal {
  :deep(.modal-content) {
    max-height: calc(100dvh - 3.5rem);
  }

  :deep(.modal-body) {
    overflow-y: auto;
  }
}

.review-table {
  :deep(.pv-datatable-container) {
    overflow: auto;
  }

  :deep(.pv-datatable-table) {
    min-width: 900px;
  }

  :deep(.pv-datatable-body-cell) {
    height: v-bind("`${ROW_HEIGHT}px`");
    padding-block: 3px;
    overflow: hidden;
  }
}

.review-text {
  display: block;

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: -2px;
  }
}

.review-registry {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;

  .review-text {
    width: 100%;
  }
}
</style>
